<script lang="ts">
  import UIRouterLink from "@shared/svelte/Components/UIRouterLink.svelte";
  import { InventoryDiscountsStore } from "@retailer/svelte/stores/InventoryDiscountsStore";
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import { slide } from "svelte/transition";
  import { pluralize } from "@shared/svelte/Modules/pluralize";
  import { debounce, groupBy } from "lodash";
  import LayoutStore from "@shared/svelte/Stores/LayoutStore";
  import type { Product } from "@shared/types/ProductLine";

  import { EventTracking } from "@shared/services/event_tracking/events";
  import {
    DealTierProductEntity,
    QuantityDiscountEntity,
  } from "@shared/services/event_tracking/entities";
  import PriceGroupDescription from "./Components/PriceGroupDescription.svelte";
  import SpecialDiscountsTooltip from "@retailer/svelte/ProductDetails/Components/SpecialDiscountsTooltip.svelte";
  import { canCalculateOunces, pricePerOunce } from "@retailer/services/unit_calculation_service";
  import { discountCasePrice, discountUnitPrice } from "./Helpers";
  import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";

  export let product: Product;

  const { trackEvent } = EventTracking;

  let debounceTrackQuantity = debounce(trackQuantityDiscounts, 250);

  let isOpen: boolean = true;

  const displayPricePerOunce = stagedRolloutCheck("display_price_per_ounce");

  function trackQuantityDiscounts(quantityDiscountIds) {
    for (const discountId of quantityDiscountIds) {
      const entities: QuantityDiscountEntity = new QuantityDiscountEntity({
        discount_id: discountId,
      });
      trackEvent("begin_to_render", [entities]);
    }
  }

  const debounceTrackMix = debounce(deals => {
    for (const deal of deals) {
      const entity: DealTierProductEntity = new DealTierProductEntity({
        product_id: product.id,
        deal_tier_id: deal.dealId,
        distributor_id: product.distributor_id,
        product_group_id: deal.discounts[0].product_group_id,
        sku: product.sku,
      });
      trackEvent("begin_to_render", [entity]);
    }
  }, 250);

  const pricePerOunceText = (product, discount) => {
    if (discount.target_container != "units" && canCalculateOunces(product, true)) {
      return `– $${pricePerOunce(
        product,
        discountCasePrice({ ...product, satisfied: undefined }, discount),
        true,
      )}/oz`;
    } else if (canCalculateOunces(product)) {
      return `– $${pricePerOunce(
        product,
        discountUnitPrice({ ...product, satisfied: undefined }, discount),
      )}/oz`;
    }
  };

  $: inventoryDiscounts = $InventoryDiscountsStore.getDiscountsForInventoryId(product.inventory_id);
  $: quantityDiscounts = inventoryDiscounts.quantityDiscounts;
  $: quantityWithRequirementsDiscountGroups =
    inventoryDiscounts.quantityWithRequirementsDiscountGroups;
  $: mixDeals = inventoryDiscounts.mixDeals;
  $: ripMixDeals = inventoryDiscounts.ripMixDeals;
  $: ripQuantityDiscountGroups = inventoryDiscounts.ripQuantityDiscountGroups;

  $: dealsCount =
    quantityDiscounts.length +
    quantityWithRequirementsDiscountGroups.length +
    mixDeals.length +
    ripMixDeals.length +
    ripQuantityDiscountGroups.length;
  $: hasAnyDiscounts =
    hasQuantityDiscounts ||
    hasMixDeals ||
    hasQuantityWithRequirementsDiscounts ||
    hasRipMixDeals ||
    hasRipQuantityDiscounts;
  $: hasQuantityDiscounts = quantityDiscounts.length > 0;
  $: hasQuantityWithRequirementsDiscounts = quantityWithRequirementsDiscountGroups.length > 0;
  $: hasMixDeals = mixDeals.length > 0;
  $: hasRipMixDeals = ripMixDeals.length > 0;
  $: hasRipQuantityDiscounts = ripQuantityDiscountGroups.length > 0;

  let hasTrackedMix = false;
  let hasTrackedQuantity = false;

  $: {
    if (hasMixDeals && !hasTrackedMix) {
      debounceTrackMix(mixDeals);
      hasTrackedMix = true;
    }
  }

  $: {
    if (hasQuantityDiscounts && !hasTrackedQuantity) {
      debounceTrackQuantity(quantityDiscounts.map(discount => discount.discount_id));
      hasTrackedQuantity = true;
    }
  }
</script>

{#if hasAnyDiscounts}
  <div class="grid-col c12-12 margin-bottom--smaller">
    <div class="media">
      <div class="media-fluid" />
    </div>

    <div class="fizz-styles product-detail-deals__wrapper">
      <details class="fizz-styles" bind:open={isOpen}>
        <summary class="fizz-summary">
          <Icon type="Deal" height="12" width="12" extraClassNames="fizz-text-deal" />
          <span class="product-detail-deals__summary-label">
            {#if !hasQuantityWithRequirementsDiscounts}
              {dealsCount}
            {/if}
            {pluralize("Deals", dealsCount)}</span
          >
        </summary>
      </details>
      {#if isOpen}
        {#if hasQuantityDiscounts || hasMixDeals}
          <div
            class="fizz-card fizz-surface-gray fizz-inset-squish-16 fizz-text-small fizz-radius-medium"
            transition:slide={{ delay: 0, duration: 200 }}
            id="mix-and-quantity-section"
          >
            {#if hasQuantityDiscounts}
              <p><strong>Quantity</strong></p>
              {#each quantityDiscounts as discount}
                <p
                  class="product-detail-deals__quantity-applied"
                  discount-id={discount.discount_id}
                >
                  {discount.discount_string}
                  {#if displayPricePerOunce}
                    {pricePerOunceText(product, discount)}
                  {/if}
                </p>
              {/each}
            {/if}

            {#if hasMixDeals}
              {#if hasQuantityDiscounts}
                <p class="fizz-stack-16" />
              {/if}
              <p>
                <strong>Mix</strong>
              </p>

              {#each mixDeals as mixDeal, i}
                {#if i > 0}
                  <br />
                {/if}
                <p
                  class="product-detail-deals__available"
                  deal-tier-id={mixDeal.discounts[0].deal_tier_id}
                >
                  {#if mixDeal.discounts[0].product_group_id}
                    <UIRouterLink
                      stateName="layout.deals_v2"
                      params={{
                        distributorId: product.distributor_id,
                        productGroupId: mixDeal.discounts[0].product_group_id,
                      }}
                      trackingParams={{
                        discountClick: true,
                        product_id: mixDeal.discounts[0].product_id,
                        distributor_id: product.distributor_id,
                        product_group_id: mixDeal.discounts[0].product_group_id,
                        sku: mixDeal.discounts[0].sku,
                      }}
                    >
                      {mixDeal.dealName}
                      {mixDeal.dealLabel === "item" ? "" : mixDeal.dealLabel} mix deal
                    </UIRouterLink>
                  {:else}
                    <UIRouterLink
                      stateName="layout.deal_detail"
                      classes="nounderline-hover"
                      params={{
                        dealId: mixDeal.discounts[0].deal_id,
                        dealTierId: mixDeal.discounts[0].deal_tier_id,
                      }}
                      trackingParams={{
                        discountClick: true,
                        product_id: mixDeal.discounts[0].product_id,
                        distributor_id: mixDeal.discounts[0].distributor_id,
                        deal_tier_id: mixDeal.discounts[0].deal_tier_id,
                        sku: mixDeal.discounts[0].sku,
                      }}
                    >
                      {mixDeal.dealName}
                      {mixDeal.dealLabel === "item" ? "" : mixDeal.dealLabel} mix deal
                    </UIRouterLink>
                  {/if}
                </p>

                {#each mixDeal.discounts as discount}
                  <p
                    class="product-detail-deals__tier-available"
                    deal-tier-id={discount.deal_tier_id}
                  >
                    {discount.discount_string}
                    {#if displayPricePerOunce}
                      {pricePerOunceText(product, discount)}
                    {/if}
                  </p>
                {/each}
              {/each}
            {/if}
          </div>
        {/if}

        {#if quantityWithRequirementsDiscountGroups.length > 0}
          {#if hasMixDeals || hasQuantityDiscounts}
            <p class="fizz-stack-8" />
          {/if}
          <div
            class="fizz-card fizz-surface-gray fizz-inset-squish-16 fizz-text-small fizz-radius-medium"
            transition:slide={{ delay: 0, duration: 200 }}
            style="overflow: visible"
          >
            <SpecialDiscountsTooltip
              horizontalAlignment={$LayoutStore.isMobile ? "left" : "center"}
            />

            {#each quantityWithRequirementsDiscountGroups as quantityWithRequirementsGroup, i}
              <p class="fizz-text-strong"
                >{quantityWithRequirementsGroup.sfPriceGroup?.name || "Special Discount"}</p
              >
              <PriceGroupDescription
                description={quantityWithRequirementsGroup.sfPriceGroup?.description || ""}
              />
              {#each quantityWithRequirementsGroup.discounts as discount}
                <p>
                  {discount.discount_string}
                  {#if displayPricePerOunce}
                    {pricePerOunceText(product, discount)}
                  {/if}
                </p>
              {/each}
              {#if i != quantityWithRequirementsDiscountGroups.length - 1}
                <p class="fizz-stack-16" />
              {/if}
            {/each}
          </div>
        {/if}

        {#if hasRipMixDeals || hasRipQuantityDiscounts}
          {#if hasMixDeals || hasQuantityDiscounts || hasQuantityWithRequirementsDiscounts}
            <p class="fizz-stack-8" />
          {/if}
          <div
            class="fizz-card fizz-inset-squish-16 fizz-text-small fizz-radius-medium"
            style="border: 1px dashed #BFBFBF"
            transition:slide={{ delay: 0, duration: 200 }}
            id="rips-section"
          >
            {#if hasRipQuantityDiscounts}
              <p>
                <strong>Quantity RIP</strong>
              </p>
              {#each ripQuantityDiscountGroups as ripQuantityDiscountGroup, i}
                {#if i > 0}
                  <br />
                {/if}
                {#each ripQuantityDiscountGroup.discounts as discount}
                  <p class="product-detail-deals__tier-available">
                    {discount.discount_string}
                  </p>
                {/each}
              {/each}
            {/if}
            {#if hasRipMixDeals}
              {#if hasRipQuantityDiscounts}
                <p class="fizz-stack-4" />
              {/if}
              <p>
                <strong>Mix RIP</strong>
              </p>

              {#each ripMixDeals as ripMixDeal, i}
                {#if i > 0}
                  <br />
                {/if}
                <p
                  class="product-detail-deals__available"
                  deal-tier-id={ripMixDeal.discounts[0].deal_tier_id}
                >
                  {#if ripMixDeal.discounts[0].product_group_id}
                    <UIRouterLink
                      stateName="layout.deals_v2"
                      params={{
                        distributorId: product.distributor_id,
                        productGroupId: ripMixDeal.discounts[0].product_group_id,
                      }}
                      trackingParams={{
                        discountClick: true,
                        product_id: product.id,
                        distributor_id: product.distributor_id,
                        product_group_id: ripMixDeal.discounts[0].product_group_id,
                        sku: ripMixDeal.discounts[0].sku,
                      }}
                    >
                      {ripMixDeal.dealName}
                      {ripMixDeal.dealLabel} mix RIP
                    </UIRouterLink>
                  {:else}
                    <UIRouterLink
                      stateName="layout.deal_detail"
                      classes="nounderline-hover"
                      params={{
                        dealId: ripMixDeal.discounts[0].deal_id,
                        dealTierId: ripMixDeal.discounts[0].deal_tier_id,
                      }}
                      trackingParams={{
                        discountClick: true,
                        product_id: ripMixDeal.discounts[0].product_id,
                        distributor_id: ripMixDeal.discounts[0].distributor_id,
                        deal_tier_id: ripMixDeal.discounts[0].deal_tier_id,
                        sku: ripMixDeal.discounts[0].sku,
                      }}
                    >
                      {ripMixDeal.dealName}
                      {ripMixDeal.dealLabel} mix RIP
                    </UIRouterLink>
                  {/if}
                </p>

                {#each ripMixDeal.discounts as discount}
                  <p
                    class="product-detail-deals__tier-available"
                    deal-tier-id={discount.deal_tier_id}
                  >
                    {discount.discount_string}
                  </p>
                {/each}
              {/each}
            {/if}
          </div>
        {/if}
      {/if}
    </div>
  </div>
{/if}
